import React, { useState, useEffect } from 'react';
import { Input } from './Input';
import { currencyMask, formatCNPJ, formatPhone, formatCPF, formatCEP } from '@/lib/formatters';

interface MaskedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  mask?: 'currency' | 'cnpj' | 'phone' | 'cpf' | 'cep';
  value?: string | number;
  onChange?: (value: string) => void;
  onValueChange?: (value: number) => void; // Para valores monetários
}

export const MaskedInput: React.FC<MaskedInputProps> = ({
  mask,
  value = '',
  onChange,
  onValueChange,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value !== undefined) {
      let formattedValue = '';
      
      if (mask === 'currency') {
        formattedValue = currencyMask(String(value));
      } else if (mask === 'cnpj') {
        formattedValue = formatCNPJ(String(value));
      } else if (mask === 'phone') {
        formattedValue = formatPhone(String(value));
      } else if (mask === 'cpf') {
        formattedValue = formatCPF(String(value));
      } else if (mask === 'cep') {
        formattedValue = formatCEP(String(value));
      } else {
        formattedValue = String(value);
      }
      
      setDisplayValue(formattedValue);
    }
  }, [value, mask]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    if (mask === 'currency') {
      // Remove tudo que não é número
      const numbers = inputValue.replace(/\D/g, '');
      
      if (numbers === '') {
        setDisplayValue('');
        onChange?.('');
        onValueChange?.(0);
        return;
      }
      
      // Converte para inteiro em centavos (elimina zeros à esquerda)
      const centsInt = parseInt(numbers, 10) || 0;
      const reaisInt = Math.floor(centsInt / 100);
      const centavosInt = centsInt % 100;
      
      // Formata para exibição
      const reaisStr = reaisInt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const centavosStr = centavosInt.toString().padStart(2, '0');
      const formattedValue = `${reaisStr},${centavosStr}`;
      
      setDisplayValue(formattedValue);
      
      onChange?.(formattedValue);
      onValueChange?.(centsInt / 100);
    } else {
      setDisplayValue(inputValue);
      onChange?.(inputValue);
    }
  };

  return (
    <Input
      {...props}
      value={displayValue}
      onChange={handleChange}
    />
  );
};
